/**
 * markdown.tsx — a small, dependency-free Markdown renderer so AI responses read
 * like documents instead of a wall of raw text. It supports the constructs a grounded
 * assistant actually emits — headings, paragraphs, bold/italic/`code`, links, ordered
 * and unordered lists, blockquotes, rules, and fenced code — and routes fenced
 * ```flow / ```ladder / ```lineage / ```diagram / ```mermaid blocks to the native {@link Diagram}.
 *
 * It builds React nodes directly (never `dangerouslySetInnerHTML`), so output is
 * XSS-safe by construction, and link hrefs are scheme-checked. The block parser is a
 * pure function ({@link parseMarkdownBlocks}) so it can be tested without a DOM.
 */
import type { ReactNode } from "react";
import { Diagram } from "./diagram.tsx";

export type Block =
  | { type: "heading"; level: number; text: string }
  | { type: "para"; text: string }
  | { type: "code"; lang: string; code: string }
  | { type: "diagram"; code: string }
  | { type: "list"; ordered: boolean; items: string[] }
  | { type: "table"; headers: string[]; rows: string[][] }
  | { type: "quote"; text: string }
  | { type: "hr" };

const DIAGRAM_LANGS = new Set(["flow", "ladder", "lineage", "diagram", "mermaid"]);
const HR = /^([-*_])\1{2,}$/;
const HEADING = /^(#{1,6})\s+(.*)$/;
const UL = /^[-*+]\s+(.*)$/;
const OL = /^\d+[.)]\s+(.*)$/;
// A GFM table needs a header row of `| a | b |` and a separator row of dashes/colons.
const TABLE_ROW = /^\|?(.+)\|?$/;
const TABLE_SEP = /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/;

/** Split a `| a | b |` row into trimmed cells (leading/trailing pipes optional). */
function tableCells(line: string): string[] {
  return line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
}

/** Pure: split markdown source into a flat list of blocks. Never throws. */
export function parseMarkdownBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, "\n").split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    const trimmed = line.trim();

    // Fenced code (``` or ~~~), possibly with a language tag.
    const fence = trimmed.match(/^(```+|~~~+)\s*(\S*)\s*$/);
    if (fence) {
      const marker = fence[1]!;
      const lang = (fence[2] ?? "").toLowerCase();
      const body: string[] = [];
      i++;
      while (i < lines.length && lines[i]!.trim() !== marker && !lines[i]!.trim().startsWith(marker)) {
        body.push(lines[i]!);
        i++;
      }
      i++; // consume closing fence (or run off the end)
      const code = body.join("\n");
      blocks.push(DIAGRAM_LANGS.has(lang) ? { type: "diagram", code } : { type: "code", lang, code });
      continue;
    }

    if (trimmed === "") {
      i++;
      continue;
    }

    if (HR.test(trimmed)) {
      blocks.push({ type: "hr" });
      i++;
      continue;
    }

    const h = trimmed.match(HEADING);
    if (h) {
      blocks.push({ type: "heading", level: h[1]!.length, text: h[2]!.trim() });
      i++;
      continue;
    }

    // Blockquote — consume consecutive `>` lines.
    if (trimmed.startsWith(">")) {
      const quote: string[] = [];
      while (i < lines.length && lines[i]!.trim().startsWith(">")) {
        quote.push(lines[i]!.trim().replace(/^>\s?/, ""));
        i++;
      }
      blocks.push({ type: "quote", text: quote.join("\n") });
      continue;
    }

    // GFM table — a header row followed by a `|---|---|` separator. Consume the
    // contiguous body rows. Defined before lists/paragraphs so pipes claim the line.
    if (trimmed.includes("|") && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1]!) && TABLE_ROW.test(trimmed)) {
      const headers = tableCells(trimmed);
      const sepCells = tableCells(lines[i + 1]!);
      // Guard: the separator must describe the same column count as the header.
      if (sepCells.length === headers.length) {
        i += 2;
        const rows: string[][] = [];
        while (i < lines.length && lines[i]!.trim() !== "" && lines[i]!.includes("|")) {
          rows.push(tableCells(lines[i]!));
          i++;
        }
        blocks.push({ type: "table", headers, rows });
        continue;
      }
    }

    // List — consume consecutive item lines of the same ordering.
    const isUl = UL.test(trimmed);
    const isOl = OL.test(trimmed);
    if (isUl || isOl) {
      const ordered = isOl;
      const items: string[] = [];
      while (i < lines.length) {
        const t = lines[i]!.trim();
        const m = ordered ? t.match(OL) : t.match(UL);
        if (!m) break;
        items.push(m[1]!);
        i++;
      }
      blocks.push({ type: "list", ordered, items });
      continue;
    }

    // Paragraph — consecutive non-blank lines that aren't another block start.
    const para: string[] = [];
    while (i < lines.length) {
      const t = lines[i]!.trim();
      const startsTable = t.includes("|") && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1]!);
      if (t === "" || HR.test(t) || HEADING.test(t) || t.startsWith(">") || UL.test(t) || OL.test(t) || /^(```+|~~~+)/.test(t) || startsTable) break;
      para.push(t);
      i++;
    }
    blocks.push({ type: "para", text: para.join("\n") });
  }
  return blocks;
}

/** Allow only safe link targets; anything else renders as plain text. */
function safeHref(href: string): string | null {
  const h = href.trim();
  if (/^(https?:|mailto:|#|\/)/i.test(h)) return h;
  return null;
}

/**
 * Parse inline markup into React nodes. Scans for the earliest of: `code`, **bold**,
 * *italic* / _italic_, [text](href). Recurses into emphasis bodies. Text is carried as
 * React string children, so it is escaped automatically.
 */
function inline(text: string, keyPrefix = "i"): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let k = 0;
  // Earliest-match scan. Each pattern captures its delimiters so we can slice cleanly.
  const patterns: { re: RegExp; render: (m: RegExpExecArray, key: string) => ReactNode }[] = [
    { re: /`([^`]+)`/, render: (m, key) => <code key={key} className="md-code">{m[1]}</code> },
    { re: /\*\*([^*]+)\*\*/, render: (m, key) => <strong key={key}>{inline(m[1]!, key)}</strong> },
    { re: /__([^_]+)__/, render: (m, key) => <strong key={key}>{inline(m[1]!, key)}</strong> },
    { re: /\*([^*]+)\*/, render: (m, key) => <em key={key}>{inline(m[1]!, key)}</em> },
    { re: /(?<![A-Za-z0-9])_([^_]+)_(?![A-Za-z0-9])/, render: (m, key) => <em key={key}>{inline(m[1]!, key)}</em> },
    {
      re: /\[([^\]]+)\]\(([^)\s]+)\)/,
      render: (m, key) => {
        const href = safeHref(m[2]!);
        return href ? (
          <a key={key} href={href} target="_blank" rel="noreferrer noopener" className="md-link">{inline(m[1]!, key)}</a>
        ) : (
          <span key={key}>{m[1]}</span>
        );
      },
    },
  ];

  while (rest.length > 0) {
    let best: { idx: number; m: RegExpExecArray; render: (m: RegExpExecArray, key: string) => ReactNode } | null = null;
    for (const p of patterns) {
      const m = p.re.exec(rest);
      if (m && (!best || m.index < best.idx)) best = { idx: m.index, m, render: p.render };
    }
    if (!best) {
      out.push(rest);
      break;
    }
    if (best.idx > 0) out.push(rest.slice(0, best.idx));
    out.push(best.render(best.m, `${keyPrefix}-${k++}`));
    rest = rest.slice(best.idx + best.m[0].length);
  }
  return out;
}

function Block({ block }: { block: Block }): JSX.Element | null {
  switch (block.type) {
    case "heading": {
      const cls = `md-h md-h${block.level}`;
      const children = inline(block.text);
      if (block.level <= 2) return <h3 className={cls}>{children}</h3>;
      if (block.level === 3) return <h4 className={cls}>{children}</h4>;
      if (block.level === 4) return <h5 className={cls}>{children}</h5>;
      return <h6 className={cls}>{children}</h6>;
    }
    case "para":
      return <p className="md-p">{inline(block.text)}</p>;
    case "code":
      return (
        <pre className="md-pre">
          <code>{block.code}</code>
        </pre>
      );
    case "diagram":
      return <Diagram source={block.code} />;
    case "list":
      return block.ordered ? (
        <ol className="md-ol">{block.items.map((it, i) => <li key={i}>{inline(it, `li${i}`)}</li>)}</ol>
      ) : (
        <ul className="md-ul">{block.items.map((it, i) => <li key={i}>{inline(it, `li${i}`)}</li>)}</ul>
      );
    case "table":
      return (
        <table className="md-table">
          <thead>
            <tr>{block.headers.map((h, i) => <th key={i}>{inline(h, `th${i}`)}</th>)}</tr>
          </thead>
          <tbody>
            {block.rows.map((row, ri) => (
              <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{inline(cell, `td${ri}-${ci}`)}</td>)}</tr>
            ))}
          </tbody>
        </table>
      );
    case "quote":
      return <blockquote className="md-quote">{inline(block.text)}</blockquote>;
    case "hr":
      return <hr className="md-hr" />;
  }
}

/**
 * Render a short value as INLINE markdown only — emphasis, code, links — with no block
 * wrapping. Used where a single line of formatted text belongs (a field value, a list
 * item, a table cell) and a `<p>` with paragraph margins would be wrong.
 */
export function InlineMarkdown({ source }: { source: string }): JSX.Element {
  return <>{inline(source)}</>;
}

/** Render AI/markdown text as a formatted document. */
export function Markdown({ source, className }: { source: string; className?: string | undefined }): JSX.Element {
  const blocks = parseMarkdownBlocks(source);
  return (
    <div className={`markdown ${className ?? ""}`}>
      {blocks.map((b, i) => (
        <Block key={i} block={b} />
      ))}
    </div>
  );
}
